import { useState, useEffect } from "react";
import {
  Lightbulb,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Target,
  BookOpen,
  RefreshCw,
  Sparkles,
  BookmarkPlus,
  BookmarkCheck,
} from "lucide-react";
import { analyzeContent, generateActionableSuggestions, type ContentAnalysis } from "../lib/topicAnalyzer";
import { fetchTopicAssistantInsights } from "../lib/openaiClient";

interface TopicAssistantProps {
  content: string;
  topic: string;
  onTopicChange: (topic: string) => void;
  onSaveSuggestion?: (payload: { title?: string; content: string }) => Promise<void> | void;
}

const aiAssistAvailable = Boolean(
  (import.meta.env.VITE_SUPABASE_FUNCTIONS_URL ?? import.meta.env.VITE_SUPABASE_URL) &&
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

export default function TopicAssistant({ content, topic, onTopicChange, onSaveSuggestion }: TopicAssistantProps) {
  const [analysis, setAnalysis] = useState<ContentAnalysis | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"overview" | "details" | "suggestions">("overview");
  const [savingSuggestionIndex, setSavingSuggestionIndex] = useState<number | null>(null);
  const [savedSuggestionMap, setSavedSuggestionMap] = useState<Record<number, boolean>>({});

  // Analyze content whenever it or topic changes
  useEffect(() => {
    if (!topic || content.length <= 50) {
      setAnalysis(null);
      setSuggestions([]);
      setIsAnalyzing(false);
      return;
    }

    let isMounted = true;
    setIsAnalyzing(true);

    const timer = setTimeout(async () => {
      try {
        let nextAnalysis: ContentAnalysis;
        let nextSuggestions: string[];

        if (aiAssistAvailable) {
          const { analysis: aiAnalysis, suggestions: aiSuggestions } = await fetchTopicAssistantInsights({
            content,
            topic,
          });
          nextAnalysis = aiAnalysis;
          nextSuggestions = aiSuggestions.length ? aiSuggestions : generateActionableSuggestions(aiAnalysis, topic);
        } else {
          const fallback = analyzeContent(content, topic);
          nextAnalysis = fallback;
          nextSuggestions = generateActionableSuggestions(fallback, topic);
        }

        if (isMounted) {
          setAnalysis(nextAnalysis);
          setSuggestions(nextSuggestions);
        }
      } catch (err) {
        console.error("Topic analysis failed", err);
        const fallback = analyzeContent(content, topic);
        if (isMounted) {
          setAnalysis(fallback);
          setSuggestions(generateActionableSuggestions(fallback, topic));
        }
      } finally {
        if (isMounted) {
          setIsAnalyzing(false);
        }
      }
    }, 500);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [content, topic, aiAssistAvailable]);

  const handleSaveSuggestion = async (suggestion: string, index: number) => {
    if (!onSaveSuggestion || !suggestion.trim()) return;
    setSavingSuggestionIndex(index);
    try {
      await onSaveSuggestion({
        title: `Topic suggestion ${index + 1}`,
        content: suggestion.trim(),
      });
      setSavedSuggestionMap((prev) => ({ ...prev, [index]: true }));
    } catch (err) {
      console.error("Failed to save topic suggestion", err);
    } finally {
      setSavingSuggestionIndex(null);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-400";
    if (score >= 60) return "text-blue-400";
    if (score >= 40) return "text-yellow-400";
    return "text-red-400";
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return "bg-green-400/20";
    if (score >= 60) return "bg-blue-400/20";
    if (score >= 40) return "bg-yellow-400/20";
    return "bg-red-400/20";
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Lightbulb className="w-5 h-5 text-white/90" />
        <h3 className="text-lg font-semibold text-white">Topic Assistant</h3>
        {isAnalyzing && <span className="ml-auto text-xs text-white/60 animate-pulse">Analyzing...</span>}
      </div>

      {/* Topic Input */}
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 mb-4 shadow-lg">
        <label className="text-sm text-white/70 mb-2 block">Paper Topic</label>
        <input
          type="text"
          value={topic}
          onChange={(e) => onTopicChange(e.target.value)}
          placeholder="Enter your paper's main topic..."
          className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white/40 transition-colors"
        />
      </div>

      {topic && content && content.length > 50 ? (
        <>
          {/* Tab Navigation */}
          <div className="flex gap-2 mb-4 border-b border-white/10">
            <button
              onClick={() => setSelectedTab("overview")}
              className={`px-3 py-2 text-sm font-medium transition-all ${
                selectedTab === "overview"
                  ? "text-white border-b-2 border-purple-400"
                  : "text-white/60 hover:text-white/80"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setSelectedTab("details")}
              className={`px-3 py-2 text-sm font-medium transition-all ${
                selectedTab === "details"
                  ? "text-white border-b-2 border-purple-400"
                  : "text-white/60 hover:text-white/80"
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setSelectedTab("suggestions")}
              className={`px-3 py-2 text-sm font-medium transition-all ${
                selectedTab === "suggestions"
                  ? "text-white border-b-2 border-purple-400"
                  : "text-white/60 hover:text-white/80"
              }`}
            >
              Suggestions {suggestions.length > 0 && <span className="ml-1 text-xs bg-purple-400 px-2 py-0.5 rounded-full text-white">{suggestions.length}</span>}
            </button>
          </div>

          {analysis && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* OVERVIEW TAB */}
              {selectedTab === "overview" && (
                <>
                  {/* Main Metrics */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Topic Relevance */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/70 flex items-center gap-1">
                          <Target className="w-3 h-3" /> Topic Relevance
                        </span>
                        <span className={`font-bold ${getScoreColor(analysis.topicRelevance)}`}>
                          {analysis.topicRelevance}%
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-400 to-purple-400 transition-all duration-300"
                          style={{ width: `${analysis.topicRelevance}%` }}
                        />
                      </div>
                    </div>

                    {/* Argument Strength */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/70 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Arguments
                        </span>
                        <span className={`font-bold ${getScoreColor(analysis.argumentStrength)}`}>
                          {analysis.argumentStrength}%
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-400 to-teal-400 transition-all duration-300"
                          style={{ width: `${analysis.argumentStrength}%` }}
                        />
                      </div>
                    </div>

                    {/* Readability */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/70 flex items-center gap-1">
                          <BookOpen className="w-3 h-3" /> Readability
                        </span>
                        <span className={`font-bold ${getScoreColor(analysis.readabilityScore)}`}>
                          {analysis.readabilityScore}%
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-orange-400 to-red-400 transition-all duration-300"
                          style={{ width: `${analysis.readabilityScore}%` }}
                        />
                      </div>
                    </div>

                    {/* Structure */}
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/70 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" /> Structure
                        </span>
                        <span className={`font-bold ${getScoreColor(analysis.structureScore)}`}>
                          {analysis.structureScore}%
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-pink-400 to-rose-400 transition-all duration-300"
                          style={{ width: `${analysis.structureScore}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quick Status */}
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                    <div className="flex items-start gap-2">
                      {analysis.topicRelevance >= 75 && analysis.argumentStrength >= 70 ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-white/80">
                            <p className="font-semibold text-green-400">Strong document!</p>
                            <p>Your paper has good topic focus and strong arguments.</p>
                          </div>
                        </>
                      ) : analysis.topicRelevance < 50 || analysis.argumentStrength < 50 ? (
                        <>
                          <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-white/80">
                            <p className="font-semibold text-red-400">Needs improvement</p>
                            <p>Focus on strengthening topic relevance and arguments.</p>
                          </div>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-white/80">
                            <p className="font-semibold text-yellow-400">Good progress</p>
                            <p>Continue developing and refining your content.</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* DETAILS TAB */}
              {selectedTab === "details" && (
                <>
                  {/* Key Information */}
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                    <h4 className="text-sm font-semibold text-white mb-2">Reading Level</h4>
                    <p className="text-xs text-white/80 mb-2">{analysis.readingLevel}</p>
                    <p className="text-xs text-white/60">Sentence Complexity: <span className="text-white/80 font-medium capitalize">{analysis.sentenceComplexity}</span></p>
                  </div>

                  {/* Key Themes */}
                  {analysis.keyThemes.length > 0 && (
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                      <h4 className="text-sm font-semibold text-white mb-2">Key Themes</h4>
                      <div className="flex flex-wrap gap-2">
                        {analysis.keyThemes.map((theme, idx) => (
                          <span key={idx} className="px-2 py-1 bg-purple-400/20 border border-purple-400/50 rounded-full text-xs text-white/90 hover:bg-purple-400/30 transition-colors">
                            {theme}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Phrases */}
                  {analysis.keyPhrases.length > 0 && (
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                      <h4 className="text-sm font-semibold text-white mb-2">Key Phrases</h4>
                      <div className="flex flex-wrap gap-2">
                        {analysis.keyPhrases.map((phrase, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-400/20 border border-blue-400/50 rounded-full text-xs text-white/90">
                            {phrase}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Missing Elements */}
                  {analysis.missingElements.length > 0 && (
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                      <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> Missing Elements
                      </h4>
                      <ul className="space-y-1">
                        {analysis.missingElements.map((element, idx) => (
                          <li key={idx} className="text-xs text-white/80 flex items-center gap-2">
                            <span className="w-1 h-1 bg-red-400 rounded-full" />
                            {element}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Suggested Sections */}
                  {analysis.suggestedSections.length > 0 && (
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg">
                      <h4 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                        <BookOpen className="w-4 h-4" /> Suggested Sections
                      </h4>
                      <ul className="space-y-1">
                        {analysis.suggestedSections.map((section, idx) => (
                          <li key={idx} className="text-xs text-white/80 flex items-center gap-2">
                            <span className="w-1 h-1 bg-green-400 rounded-full" />
                            {section}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {/* SUGGESTIONS TAB */}
              {selectedTab === "suggestions" && (
                <>
                  {suggestions.length > 0 ? (
                    <div className="space-y-2">
                      {suggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20 rounded-lg p-3 shadow-lg transition-all duration-200 cursor-pointer group"
                        >
                          <p className="text-sm text-white/90 group-hover:text-white transition-colors">{suggestion}</p>
                          {onSaveSuggestion && (
                            <button
                              type="button"
                              onClick={() => handleSaveSuggestion(suggestion, idx)}
                              disabled={savingSuggestionIndex === idx || !!savedSuggestionMap[idx]}
                              className="mt-3 inline-flex items-center gap-1 text-[11px] text-amber-200 border border-amber-400/40 rounded-full px-3 py-1 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {savedSuggestionMap[idx] ? (
                                <>
                                  <BookmarkCheck className="w-3 h-3" /> Saved
                                </>
                              ) : (
                                <>
                                  <BookmarkPlus className="w-3 h-3" />
                                  {savingSuggestionIndex === idx ? "Saving..." : "Save to Scratch"}
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-white/50">
                      <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No suggestions at this time</p>
                      <p className="text-xs mt-1">Your document looks great!</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Empty State */}
          <div className="flex-1 flex items-center justify-center text-center">
            <div className="text-white/50">
              <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-30" />
              {!topic ? (
                <>
                  <p className="text-sm font-medium">Enter your paper topic</p>
                  <p className="text-xs mt-1">Get AI-powered analysis and suggestions</p>
                </>
              ) : content.length < 50 ? (
                <>
                  <p className="text-sm font-medium">Start writing your document</p>
                  <p className="text-xs mt-1">At least 50 characters needed for analysis</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">Analyzing your document...</p>
                  <RefreshCw className="w-6 h-6 mx-auto mt-2 animate-spin" />
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
